"use client";
import React, { FC, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { ShowWhen, Toggle } from "@/components/common";
import { ActionItem } from "./item";
import { DeleteIcon, DotsIcon, EditIcon } from "@/components/assets/icons";

// hooks
import { useOutsideClickDetector } from "@/hooks/useOutsideClickDetector";
import classNames from "classnames";

const actionsList = [
  {
    icon: <DeleteIcon />,
    label: "delete",
    bgColor: "bg-[#FDF1F1]",
  },
  {
    icon: <EditIcon />,
    label: "edit",
    bgColor: "bg-[#F9F9F9]",
  },
];

type TProps = {
  onEdit?: () => void;
  onDelete?: () => void;
  onToggle?: () => void;
  isOnToggle?: boolean;
  showBorder?: boolean;
  isZeroIndex?: boolean;
};

const Actions: FC<TProps> = ({
  onEdit,
  onDelete,
  onToggle,
  isOnToggle,
  showBorder = true,
  isZeroIndex = false,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  const t = useTranslations();

  useOutsideClickDetector(actionsRef, () => {
    setIsOpened(false);
  });

  const handleToggle = () => setIsOpened((prev) => !prev);

  return (
    <div ref={actionsRef} className="relative cursor-pointer">
      <span onClick={handleToggle}>
        <DotsIcon />
      </span>
      <ShowWhen condition={isOpened}>
        <div
          className={classNames(
            "absolute right-0 z-20 w-[146px] rounded-[20px] border border-[#f9f9f9] bg-white p-2 shadow",
            {
              "top-0": isZeroIndex,
              "bottom-0": !isZeroIndex,
            },
          )}
        >
          <ul className="w-full">
            <ShowWhen condition={typeof onDelete === "function"}>
              <ActionItem
                item={actionsList[0]}
                showBorder={showBorder}
                onClick={() => {
                  onDelete();
                  setIsOpened(false);
                }}
              />
            </ShowWhen>
            <ShowWhen condition={typeof onEdit === "function"}>
              <ActionItem
                item={actionsList[1]}
                showBorder={showBorder}
                onClick={() => {
                  onEdit();
                  setIsOpened(false);
                }}
              />
            </ShowWhen>
            <ShowWhen condition={typeof onToggle === "function"}>
              <li className="pt-2">
                <Toggle
                  noBorder
                  title={t("make_default")}
                  isOn={isOnToggle}
                  handleToggle={onToggle}
                />
              </li>
            </ShowWhen>
          </ul>
        </div>
      </ShowWhen>
    </div>
  );
};

export { Actions };
